
import React from 'react';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ViewType } from './Scheduler';
import { getCurrentSingaporeDate } from '../lib/dateUtils';
import { useIsMobile } from '@/hooks/use-mobile';

interface DateNavigatorProps {
  currentDate: Date;
  currentView: ViewType;
  onDateChange: (date: Date) => void;
  className?: string;
}

const DateNavigator = ({ currentDate, currentView, onDateChange, className }: DateNavigatorProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = React.useState(currentDate.getMonth());
  const isMobile = useIsMobile();

  // Generate year options (current year ± 5 years)
  const currentYear = getCurrentSingaporeDate().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  // Month options
  const monthOptions = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' },
  ];

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      onDateChange(date);
      setIsOpen(false);
    }
  };

  const handleYearChange = (year: string) => {
    const newYear = parseInt(year);
    setSelectedYear(newYear);
    const newDate = new Date(newYear, selectedMonth, 1);
    onDateChange(newDate);
  };

  const handleMonthChange = (month: string) => {
    const newMonth = parseInt(month);
    setSelectedMonth(newMonth);
    const newDate = new Date(selectedYear, newMonth, 1);
    onDateChange(newDate);
  };

  const formatDisplayDate = () => {
    if (currentView === 'daily') {
      return isMobile ? format(currentDate, 'EEE, d MMM') : format(currentDate, 'PPP');
    } else if (currentView === 'weekly') {
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day;
      startOfWeek.setDate(diff);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return isMobile
        ? `${format(startOfWeek, 'd MMM')} - ${format(endOfWeek, 'd MMM')}`
        : `${format(startOfWeek, 'MMM d')} - ${format(endOfWeek, 'MMM d, yyyy')}`;
    } else {
      return isMobile ? format(currentDate, 'MMM yyyy') : format(currentDate, 'MMMM yyyy');
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "min-w-0 w-[10.75rem] sm:w-auto sm:min-w-[240px] justify-between text-left font-normal h-9",
            "bg-gray-50 border-gray-200 hover:bg-gray-100 transition-colors",
            "px-2 sm:px-3",
            className
          )}
        >
          <div className="flex items-center gap-1 sm:gap-2">
            <CalendarIcon className="h-4 w-4 text-gray-500 shrink-0" />
            <span className="text-xs sm:text-sm font-medium truncate">{formatDisplayDate()}</span>
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(95vw,370px)] p-0" align="start">
        <div className="p-4">
          {/* Header with Year/Month Selection */}
          <div className="flex items-center justify-between mb-4">
            <Select value={selectedMonth.toString()} onValueChange={handleMonthChange}>
              <SelectTrigger className="w-[140px] h-9 text-sm font-medium">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((month) => (
                  <SelectItem key={month.value} value={month.value.toString()}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedYear.toString()} onValueChange={handleYearChange}>
              <SelectTrigger className="w-[90px] h-9 text-sm font-medium">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Calendar */}
          <CalendarComponent
            mode="single"
            selected={currentDate}
            onSelect={handleDateSelect}
            month={new Date(selectedYear, selectedMonth)}
            onMonthChange={(date) => {
              if (date) {
                setSelectedYear(date.getFullYear());
                setSelectedMonth(date.getMonth());
              }
            }}
            initialFocus
            className="rounded-md border-0"
          />

          {/* Quick Actions */}
          <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-100">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDateSelect(getCurrentSingaporeDate())}
              className="h-8 text-xs font-medium"
            >
              Today
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-8 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateNavigator;
